'use client';

import {
  ComponentType,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react';
import { useRouter } from 'next/navigation';

import { ROUTES } from 'constant/routes';

import { useAppSelector } from 'store/hooks';

import {
  clearPostAuthReturnUrl,
  resolvePostAuthReturnUrl,
  takePostAuthReturnUrl
} from 'utils/postAuthReturnUrl';

import { LocationFlowType } from 'types';

import { Loader } from 'ui/Loader';

interface AuthProps {
  children: ReactNode;
}

const withPublicRouting = (WrappedComponent: ComponentType<AuthProps>) => {
  const AuthComponent = ({ children, ...props }: AuthProps) => {
    const router = useRouter();
    const { userInfo } = useAppSelector((state) => state.persistAuth);
    const { user, isLoading, redirectFlow, isWelcomePage } = useAppSelector(
      (state) => state.user
    );
    const { redirectParams } = useAppSelector(
      (state) => state.bookingAppointment
    );

    const isSessionRestoring = Boolean(userInfo && !user);
    const hasRedirectedRef = useRef(false);

    const defaultReturnPath = useMemo((): string => {
      if (redirectFlow === LocationFlowType.BOOK) {
        const qs = redirectParams?.trim();
        return qs ? `${ROUTES.booking}?${qs}` : ROUTES.booking;
      }
      switch (redirectFlow) {
        case LocationFlowType.GIFT_CARDS:
          return ROUTES.giftCardsShopSteps.details;
        case LocationFlowType.PACKAGES_PROMO:
          return ROUTES.packagesShop;
        case LocationFlowType.PACKAGES:
          return ROUTES.packagesShop;
        case LocationFlowType.PRODUCT:
          return ROUTES.productsShop;
        case LocationFlowType.BASKET:
          return ROUTES.cart;
        case LocationFlowType.MEMBERSHIPS:
          return ROUTES.membershipShop;
        default:
          return ROUTES.dashboard;
      }
    }, [redirectFlow, redirectParams]);

    useLayoutEffect(() => {
      if (!user || hasRedirectedRef.current) {
        return;
      }

      let destination: string | null = null;

      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('next');

        if (raw) {
          destination = resolvePostAuthReturnUrl(raw);
          clearPostAuthReturnUrl();
        }
      }

      if (!destination) {
        const fromSession = takePostAuthReturnUrl();

        if (fromSession) {
          destination = resolvePostAuthReturnUrl(fromSession);
        }
      }

      if (!destination) {
        destination = isWelcomePage ? ROUTES.welcome : defaultReturnPath;
      }

      hasRedirectedRef.current = true;
      router.replace(destination);
    }, [user, router, isWelcomePage, defaultReturnPath]);

    // Authenticated (redirecting away) or restoring a persisted session: replace
    // the page with a loader. A transient `isLoading` (login submit, background
    // prefetch) must NOT swap out the subtree — that would unmount/remount the
    // page and re-fire its mount effects in a loop. Overlay a loader instead.
    if (user || isSessionRestoring) {
      return <Loader fixed />;
    }

    return (
      <>
        <WrappedComponent {...props}>{children}</WrappedComponent>
        {isLoading && <Loader fixed bg='blackOpacity.10' />}
      </>
    );
  };

  return AuthComponent;
};

export default withPublicRouting;
